import * as STD from '@emersya/mpa-std';
import {IBasketProceedCheckoutTwoTimesBasketDescriptor} from '@emersya/mpa-basket';
import * as CM from '@emersya/mpa-components-manager';
import * as MPAEngine from '@emersya/mpa-engine/src/app/client/index';
import {PMS} from '@emersya/mpa-engine/src/app/client/components/Application/Application';
import Viewer, * as ViewerNS from '@emersya/mpa-engine/src/app/client/components/viewer/Viewer/Viewer';
import {
	Block,
	// InputButton,
	// MenuItemSelector,
	// MenuPanel,
	ModalsZone,
	Loader as LoaderNS,
	// Text
} from '@emersya/mpa-components';
import registerTotalExpoViewerPlannerFloorEditor from '../../modules/totalExpoPlannerFloorEditor';
import registerTotalExpoViewerPlannerScreenEditor from '../../modules/totalExpoPlannerScreenEditor';
import registerTotalExpoViewerPlannerPlantEditor from '../../modules/totalExpoPlannerPlantEditor';
import {ScreenshotsButton} from '@emersya/mpa-engine/src/app/client/components/viewer';
import {NS as PlannerFloorEditorNS} from '../PlannerFloorEditor/PlannerFloorEditor';

declare type TViewpoint =
	'top'
	| 'front'
	| 'frontLeft'
	| 'frontRight'
	| 'left'
	| 'right'
	| 'back'
	| 'backLeft'
	| 'backRight';

export class TotalExpoMPAEngineApplicationComponent extends MPAEngine.Application.Component<
	TotalExpoMPAEngineApplicationComponent,
	MPAEngine.Application.Configuration
> {
	private raisedFloorCategory!: Viewer.ProductCategory;
	private raisedFloorRootNode!: Viewer.ProductInstance;

	static override initialize() {
		// registerModuleX();
		registerTotalExpoViewerPlannerFloorEditor();
		registerTotalExpoViewerPlannerScreenEditor();
		registerTotalExpoViewerPlannerPlantEditor();
	};

	override updateGlobalProductName() {
		/** do nothing. **/
	};

	get user() {
		const user = this.clientAppParameters.user.name;
		if (!user) {
			return null;
		}
		return {
			name: user,
			role: this.component_configuration.users[user] || null
		};
	};

	private productAddedOrUsedReceived = false;

	override async getAndSerializeAllUserInputsData() {
		const materialsTypesComponent = this.component_find('@.modules:panel.totalExpo-viewer-planner-floor-editor.content:content.type.content');
		const selectedTypes = (<CM.DOMComponent<any, any, any>[]>materialsTypesComponent.getAllChilds()).filter(x => x.component_DOM.classList.contains('selected'));
		const UIS = await super.getAndSerializeAllUserInputsData();
		if (selectedTypes.length) {
			UIS.push(...selectedTypes.map(x => [x.component_getPath(), x.id] as [string, string]));
		}
		return UIS;
	}

	override async basketUpdate(event: STD.events.Event<any, CM.Component<any, any>>) {
		if (this.isReloading && !this.productAddedOrUsedReceived) {
			await (<PlannerFloorEditorNS.Component>this.component_find('@modules:panel.totalExpo-viewer-planner-floor-editor.content')).updateOptions(event);
			this.productAddedOrUsedReceived = true;
		}
		await super.basketUpdate(event);
		if (event.subType === 'productUsed') {
			if (!this.isReloading) {
				const component = <PlannerFloorEditorNS.Component>this.component_find('@modules:panel.totalExpo-viewer-planner-floor-editor.content');
				await component.reset(event);
			}
			if (this.hasThisModuleUsed('totalExpo-viewer-planner-floor-editor')) {
				await this.raisedFloorViewerNodeInsert();
			}
		}
	};

	private createRaisedFloorCategory() {
		const requiredParts = ['ruler_reglette', 'ruler_corner_1', 'ruler_corner_2'] as const;
		const objects = <Record<typeof requiredParts[number], PMS.Object>>Object.fromEntries(
			this.component_configuration.PMS.objects.filter(obj => obj.type === '@raised-floor').map(obj => [obj.SKU, obj])
		);
		for (const key of requiredParts) {
			if (!(key in objects)) {
				throw this.component_throwUserError('Missing raised floor parts', {
					requiredParts
				});
			}
		}
		const categoryDescriptor: Viewer.ProductCategory.IDescriptor & {
			products: NonNullable<Viewer.ProductCategory.IDescriptor['products']>;
		} = {
			name: 'raised-floor',
			menuActions: {
				edit: false,
				delete: false,
				rotate: false,
				duplicate: false
			},
			products: []
		};

		const id = <string>this.parameters.id;
		let product;
		if (id) {
			product = this.component_configuration.PMS.products.find(x => x.SKU === id);
		} else {
			product = this.component_configuration.PMS.products[0];
		}
		const productBoundingBox = product!.d3.boundingBox;
		const regletteBoundingBox = objects['ruler_reglette'].d3.boundingBox;
		const createRegletteDescriptor = (
			side: 'front' | 'back' | 'left' | 'right',
			position: Viewer.TVector3,
			rotation: number
		) => ({
			SKU: objects['ruler_reglette'].SKU,
			projectCode: objects['ruler_reglette'].d3.projectCode,
			localId: `${side}-reglette`,
			parentLocalId: 'global',
			snappable: false,
			fixedPositionSetIds: null,
			draggable: false,
			doNotListAsProductComponent: true,
			matrix: this.viewer.mtx(
				position,
				[side === 'front' || side === 'back' ? productBoundingBox.depth : productBoundingBox.width, 1, 1],
				rotation
			)
		});
		const addCornerDescriptors = (
			nodes: Viewer.Product.INode[],
			side: 'front-left' | 'front-right' | 'back-left' | 'back-right',
			position: Viewer.TVector3,
			rotation: number
		) => {
			nodes.push(this.viewer.createEmptyNodeDescriptor(
				`${side}-corner`,
				'global',
				this.viewer.mtx(position, [1, 1, 1], rotation)
			));
			nodes.push({
				SKU: objects['ruler_corner_1'].SKU,
				projectCode: objects['ruler_corner_1'].d3.projectCode,
				localId: `${side}-corner-1`,
				parentLocalId: `${side}-corner`,
				snappable: false,
				fixedPositionSetIds: null,
				draggable: false,
				doNotListAsProductComponent: true,
				matrix: this.viewer.mtx(
					[0, 0, 0],
					[1, 1, 1],
					0
				)
			});
			nodes.push({
				SKU: objects['ruler_corner_2'].SKU,
				projectCode: objects['ruler_corner_2'].d3.projectCode,
				localId: `${side}-corner-2`,
				parentLocalId: `${side}-corner`,
				snappable: false,
				fixedPositionSetIds: null,
				draggable: false,
				doNotListAsProductComponent: true,
				matrix: this.viewer.mtx(
					[0, 0, 0],
					[1, 1, 1],
					0
				)
			});
		};
		const nodes = [
			this.viewer.createEmptyNodeDescriptor('global', 'root', 0),
			createRegletteDescriptor('front', [productBoundingBox.width / 2, -regletteBoundingBox.height, productBoundingBox.depth / 2], 90),
			createRegletteDescriptor('back', [-productBoundingBox.width / 2, -regletteBoundingBox.height, -productBoundingBox.depth / 2], 270),
			createRegletteDescriptor('left', [-productBoundingBox.width / 2, -regletteBoundingBox.height, productBoundingBox.depth / 2], 0),
			createRegletteDescriptor('right', [productBoundingBox.width / 2, -regletteBoundingBox.height, -productBoundingBox.depth / 2], 180),
		];
		addCornerDescriptors(
			nodes,
			'front-left',
			[productBoundingBox.width / 2, -regletteBoundingBox.height, productBoundingBox.depth / 2 + regletteBoundingBox.depth],
			90
		);
		addCornerDescriptors(
			nodes,
			'front-right',
			[productBoundingBox.width / 2 + regletteBoundingBox.depth, -regletteBoundingBox.height, -productBoundingBox.depth / 2],
			180
		);
		addCornerDescriptors(
			nodes,
			'back-left',
			[-productBoundingBox.width / 2 - regletteBoundingBox.depth, -regletteBoundingBox.height, productBoundingBox.depth / 2],
			0
		);
		addCornerDescriptors(
			nodes,
			'back-right',
			[-productBoundingBox.width / 2, -regletteBoundingBox.height, -productBoundingBox.depth / 2 - regletteBoundingBox.depth],
			270
		);
		categoryDescriptor.products.push({
			SKU: 'raised_floor',
			nodes
		});
		this.raisedFloorCategory = this.viewer.createCategory(categoryDescriptor);
	};


	private async raisedFloorViewerNodeInsert() {
		const product = this.currentProduct;
		if (this.raisedFloorRootNode || !product || !this.viewer.planner) {
			if (this.isReloading) {
				return;
			}
			this.component_throwUserError('', {});
		}
		const raisedFloorSchema = this.raisedFloorCategory.products.find(product => product.SKU === 'raised_floor');
		if (!raisedFloorSchema) {
			this.component_throwLibraryError('', {});
		}
		if (this.isReloading) {
			if (!this.previousConfigurationMetadata) {
				this.component_throwLibraryError('', {});
			}
			const vPath = this.viewer.component_getPath();
			const vUI: [string, ViewerNS.IViewerSerialized] | undefined = <any>this.previousConfigurationMetadata.userInputs.find(
				x => x[0] === vPath);
			if (!vUI) {
				this.component_throwLibraryError('', {});
			}
			const savedInstance = vUI[1].productInstances.find(x => x.product === raisedFloorSchema.SKU);
			if (!savedInstance) {
				this.component_throwLibraryError('No raised flood node found!', {});
			}
			const allInstances = Array.from(this.viewer.safe.value.productInstances);
			const node = allInstances.find(x => x.localId === savedInstance.localId);
			if (!node) {
				this.component_throwUserError('', {});
			}
			this.raisedFloorRootNode = node;
		} else {
			this.raisedFloorRootNode = await raisedFloorSchema.instantiate(
				this.viewer.globalLocalId,
				'insert',
				null,
				false,
				{}
			);
		}
		if (!this.raisedFloorRootNode) {
			this.component_throwLibraryError('', {});
		}
		this.raisedFloorRootNode.setName('Raised floor');
	};

	async setRaisedFloorVisibility(isVisible: boolean, hasChanged : boolean) {
		if (this.currentProduct) {
			const floorOption = this.currentProduct.getPart(['floor']).currentOption;
			if (floorOption) {
				if (!floorOption.metaData) {
					floorOption.metaData = {};
				}
				(<any>floorOption.metaData).isRaised = isVisible;
			}
		}
		if (!this.isReloading && hasChanged) {
			await this.viewer.setProductNodesInstancesMatrices([
				{
					localId: this.raisedFloorRootNode.localId,
					matrix: Viewer.mtx(
						[0, 0, 0],
						isVisible ? [1, 1, 1] : [0, 0, 0],
						0
					)
				}
			]);
		}
	};

	override async component_start(event: STD.events.Event<any, any>) {
		let productId = <string>this.parameters.id;
		if (!productId) {
			if (this.parameters.test && this.basket!.basket.products[0]?.id) {
				productId = this.basket!.basket.products[0]!.id;
			}
		}
		if (!productId) {
			const loader = <LoaderNS.Component | undefined>this.component_outerChilds.find(
				x => x instanceof LoaderNS.Component
			);
			await loader?.component_start(event);
			await this.modals.component_start(event);
			await loader?.component_disable();
			await this.modals.useModal('modalError', {
				error: 'Cant open app without a product id in URL, add ?id=YourProductId to URL to open the app with the product',
				canBeClosed: false
			});
			return;
		} else if (!this.basket!.basket.products.find(x => x.id === productId)) {
			await this.modals.useModal('modalError', {
				error: 'Your product cant be found, make sure it exists in configuration and that you wrote it correctly in the URL like this id=YourProductId',
				canBeClosed: false
			});
			return;
		}
		try {
			this.createRaisedFloorCategory();
			await this.basket!.basket.useProduct(productId);
			const isReloading = this.isReloading;
			await super.component_start(event);
			if (!isReloading) {
				await this.basket!.basket.useProduct(productId);
			}
		} catch (error) {
			const loader = <LoaderNS.Component | undefined>this.component_outerChilds.find(
				x => x instanceof LoaderNS.Component
			);
			if (loader && !loader.component_isStarted) {
				await loader?.component_start(event);
			}
			if (!this.modals.component_isStarted) {
				await this.modals.component_start(event);
			}
			await loader?.component_disable();
			void this.modals.useModal('modalError', {
				error: 'Unexpected error!',
				canBeClosed: false
			});
			throw error;
		}
		/** Furniture { X; Z } rotations are disabled in this app. **/
		{
			await this.component_find('@.visualization.V.viewer-menu:edit.content.rotation.x').component_disable();
			await this.component_find('@.visualization.V.viewer-menu:edit.content.rotation.z').component_disable();
		}
		await this.basket!.basket.activatePriceComputations();
		this.syncPriceComponentWithBasket();
	};

	override async component_initialize() {
		const modalZone = <ModalsZone.Component>this.component_find('K.modals');
		if (!modalZone) {
			this.component_throwUserError('Missing modal zone', {});
		}
		await modalZone.component_instantiateOuterChild({
			type: 'modal-error',
			id: 'modalError',
			tree: this.component_tree,
			slot: null,
			isSlot: false,
			configuration: {}
		});
		await modalZone.component_instantiateOuterChild({
			type: 'modal-order-confirmed',
			id: 'modalOrderConfirmed',
			tree: this.component_tree,
			slot: null,
			isSlot: false,
			configuration: {}
		});
		await super.component_initialize();
		const processBtn = this.component_find('K.visualization.basket.processOrder');
		const event = this.component_getEventWhichRepresentNoEvent();
		await processBtn.component_initialize();
		await processBtn.component_start(event);
		await processBtn.component_enable();
		const visualization = <Block.Component>this.component_find('K.visualization');
		const screenshotButton = await visualization.component_instantiateOuterChild({
			type: 'screenshotsButton',
			id: 'screenshotsButton',
			configuration: <ScreenshotsButton.Configuration>{
				URL: '$ASSETS/camera-solid-full.svg'
			},
			tree: visualization,
			slot: null,
			isSlot: false,
			parentNode: visualization.component_DOM
		});
		await screenshotButton.component_initialize();
		await screenshotButton.component_start(event);
	};

	getCurrentProductBoundingBox() {
		const currentProduct = this.currentProduct;
		if (!currentProduct) {
			this.component_throwLibraryError('This method should only be called when a current product is defined', {});
		}
		const productBoundingBox = this.component_configuration.PMS.products.find(x => x.SKU === currentProduct.id);
		if (!productBoundingBox) {
			this.component_throwLibraryError('Current product could not be found in PMS products', {});
		}
		return productBoundingBox.d3.boundingBox;
	};

	computeStandBoundingBoxTotalExpo = () => {
		const {width: x, depth: z, height: y} = this.getCurrentProductBoundingBox();
		const bb = {
			width: 0,
			height: 0,
			depth: 0,
			maxX: 0.5 * x,
			maxY: y,
			maxZ: 0.5 * z,
			minX: -0.5 * x,
			minY: -0.005,
			minZ: -0.5 * z
		};
		bb.width = bb.maxX - bb.minX;
		bb.height = bb.maxY - bb.minY;
		bb.depth = bb.maxZ - bb.minZ;
		return (bb);
	};

	override getScreenshotViewpoint(
		from: TViewpoint
	) {
		const standBB = this.computeStandBoundingBoxTotalExpo();
		const diameter = Math.max(standBB.depth, standBB.width);
		const standCenter = {
			x: standBB.minX + (standBB.width / 2.0),
			y: standBB.minY + (standBB.height / 2.0),
			z: standBB.minZ + (standBB.depth / 2.0)
		};
		const d = diameter / .8;
		let x: number = 0;
		let y: number = standBB.height / 2.0;
		let z: number = 0;
		switch (from) {
			case 'front' : {
				z = d;
				break;
			}
			case 'frontLeft' : {
				const p0 = <[number, number]>[0, d];
				const o = <[number, number]>[standCenter.x, standCenter.z];
				const p1 = STD.math.rotatePointOnCircle(o, p0, 45);
				x = p1.x;
				z = p1.y;
				break;
			}
			case 'frontRight' : {
				const p0 = <[number, number]>[0, d];
				const o = <[number, number]>[standCenter.x, standCenter.z];
				const p1 = STD.math.rotatePointOnCircle(o, p0, -45);
				x = p1.x;
				z = p1.y;
				break;
			}
			case 'left' : {
				x = -d;
				break;
			}
			case 'right' : {
				x = d;
			}
				break;
			case 'back' : {
				z = -d;
				break;
			}
			case 'backLeft' : {
				const p0 = <[number, number]>[0, -d];
				const o = <[number, number]>[standCenter.x, standCenter.z];
				const p1 = STD.math.rotatePointOnCircle(o, p0, -45);
				x = p1.x;
				z = p1.y;
				break;
			}
			case 'backRight' : {
				const p0 = <[number, number]>[0, -d];
				const o = <[number, number]>[standCenter.x, standCenter.z];
				const p1 = STD.math.rotatePointOnCircle(o, p0, 45);
				x = p1.x;
				z = p1.y;
				break;
			}
			case 'top' : {
				x = standCenter.x;
				y = d * 2;
				z = standCenter.y;
				break;
			}
			default:
				throw new Error;
		}
		return {
			position: <Viewer.TVector3>[x, y, z],
			target: <Viewer.TVector3>[standCenter.x, standCenter.y, standCenter.z],
			up: <Viewer.TVector3>[0, 1, 0]
		};
	};

	override async processOrder(): Promise<void> {
		if (!this.basket) {
			this.component_throwUserError('Cannot process order for an app without basket!', {});
		}
		const loader = (<LoaderNS.Component>this.component_find('@loader'));
		await loader.update('basketCreation', null);
		const currentProduct = this.currentProduct;
		if (!currentProduct) {
			this.component_throwUserError('Process order but there are no current product!', {});
		}
		const screenshotsToBeDone = <TViewpoint[]>['frontLeft', 'front', 'frontRight'];
		if (!screenshotsToBeDone) {
			this.component_throwLibraryError('', {});
		}
		const screenshots = await screenshotsToBeDone.reduce<Promise<string[]>>(async (p, side) => {
			const a = await p;
			a.push((await this.viewer.takeSaveScreenshotGetURL({
				takeBackground: false,
				width: 512,
				height: 256,
				camera: this.getScreenshotViewpoint(side)
			})).screenshot);
			return a;
		}, Promise.resolve([]));
		await currentProduct.setThumbnailsURLs(screenshots);
		const save = await this.viewer.saveModularConfiguration({
			screenshot: true,
			shortLink: true,
			unique: true
		});
		await currentProduct.setConfigurationCode(save.code);
		await currentProduct.setConfigurationReplayURL(<string>save.shortLink);
		if (!this.basket.basket.metaData) {
			this.basket.basket.metaData = {};
		}
		const md = <any>this.basket.basket.metaData;
		md.replay = await this.generateConfigurationMetadata();
		let basketCode;
		const OMSMode = this.component_configuration.OMSMode || 'quote';
		const [isQuoteRequest, isOrderRequest] = this.parameters.mode === 'external' ? [OMSMode === 'quote', OMSMode === 'order'] : [false, false];
		await this.basket.basket.proceedCheckout(<IBasketProceedCheckoutTwoTimesBasketDescriptor>{
			builderType: `engine<${this.version}>-totalExpo<>`,
			isQuoteRequest: isQuoteRequest,
			isOrderRequest,
			useTwoTimesCreation: true,
			twoTimesCallback: async (BC: string) => {
				basketCode = BC;
				const replayURL = STD.net.URL_format(`${this.getAppURL().
				replace(this.code, 'replay')}/${this.code}`, {
					basketCode,
					mode: 'external',
					id: currentProduct.reference!.value
				});
				await currentProduct.setConfiguratorReplayURL(replayURL);
			}
		});
		const parent = window.parent;
		if (!parent) {
			this.component_throwUserError('Missing parent window', {});
		}
		if (!this.basket) {
			this.component_throwLibraryError('', {});
		}
		const basketForIntegration = {...await this.basket.basket.serialize()};
		delete (<any>basketForIntegration).metaData;
		(<any>basketForIntegration).code = basketCode;
		parent.postMessage({
			action: 'onProcessOrder',
			basket: basketForIntegration
		}, '*');
		await loader.update('processCheckout', null);
		if (!this.currentProduct || !this.currentProduct.configurationCode) {
			this.component_throwLibraryError('', {});
		}
		// await loader.update('getScreenshots', null);
		// if (!currentProduct.configuratorReplayURL) {
		// 	this.component_throwLibraryError('', {});
		// }
		await loader.update('finalisation', null);
		await loader.component_disable();
		await this.modals.useModal('modalOrderConfirmed', {
			replayURL: this.parameters.mode === 'internal' ? this.currentProduct.configuratorReplayURL : null
		});
	};

	deduceIsRaised() {
		const floorComponent = <PlannerFloorEditorNS.Component>this.component_find('@modules:panel.totalExpo-viewer-planner-floor-editor.content');
		if (floorComponent.isRaised) {
			return 'enabled';
		} else {
			return 'disabled';
		}
	};

	onProductUsed() {
	};

	async onShare() {
		await this.modals.useModal('product-replayLinkModal', 'TODO');
	}

	async setProductNodeDraggable(draggable: boolean) {
		const product = this.currentProduct;
		if (!product) {
			this.component_throwLibraryError('', {});
		}
		const list = product.parts.filter(x => x.metaData);
		const nodes: Viewer.INative_setProductNodesInstancesDraggable_descriptor = {
			nodes: list.map(x => ({localId: (<any>x.metaData).nodeLocalId, draggable: draggable}))
		};
		if (nodes.nodes.length != 0) {
			await this.viewer.setProductNodesInstancesDraggable(nodes);
		}
	};

	protected override onExit() {
	};

	constructor(
		descriptor: ConstructorParameters<typeof CM.Component> [0],
		configuration: MPAEngine.Application.Configuration
	) {
		super(descriptor, configuration);
		if (!this.parameters.mode || !['internal', 'external'].includes(<string>this.parameters.mode)) {
			this.parameters.mode = 'internal';
		}
	};
}

export namespace NS {
	export const Component = TotalExpoMPAEngineApplicationComponent;
	export type Component = TotalExpoMPAEngineApplicationComponent;
	export type Configuration = MPAEngine.Application.Configuration;
}

CM.Manager.declare({
	id: 'mpaengine-application-total-expo-stand-configurator-v2-2025',
	type: TotalExpoMPAEngineApplicationComponent
});
